"use client";

import React, { useState } from "react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { ChevronsLeftRight, Droplets, Leaf, Wind, Zap } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ChartConfig, ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const chartData = [{
  month: "Jan",
  clean: 100,
  dirty: 100
}, {
  month: "Feb",
  clean: 100,
  dirty: 96
}, {
  month: "Mar",
  clean: 100,
  dirty: 91
}, {
  month: "Apr",
  clean: 100,
  dirty: 85
}, {
  month: "May",
  clean: 100,
  dirty: 78
}, {
  month: "Jun",
  clean: 100,
  dirty: 70
}];
const chartConfig = {
  clean: {
    label: "Clean Panels (Optimal)",
    color: "var(--chart-1)",
    icon: Zap
  },
  dirty: {
    label: "Dirty Panels (Degraded)",
    color: "var(--chart-4)",
    icon: TrendingUpIcon
  }
} satisfies ChartConfig;
function TrendingUpIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </svg>;
}
const debrisTypes = [{
  title: "Dust & Airborne Particulates",
  icon: Wind,
  content: "Even a thin layer of dust can significantly scatter sunlight before it reaches the photovoltaic cells. In dry areas, this accumulation can reduce your system's overall energy output by up to 20% over a few months."
}, {
  title: "Bird Droppings",
  icon: Droplets,
  content: "Highly opaque and acidic, bird droppings create hard shadows on individual cells. This not only blocks energy production but can cause 'hot spots' that permanently damage the panel glass if left untreated."
}, {
  title: "Pollen, Leaves & Sap",
  icon: Leaf,
  content: "Seasonal debris often sticks to panels, especially after light rain or morning dew. Tree sap acts like an adhesive, trapping other dirt and creating a stubborn layer that natural rain cannot wash away."
}];
export default function MaximizingYourEnergyOutput() {
  const [sliderPosition, setSliderPosition] = useState(50);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSliderPosition(Number(e.target.value));
  };
  return <section id="maximizing-your-energy-output" className="relative w-full overflow-hidden bg-background py-20 lg:py-32">
      {}
      <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[var(--primary)]/5 rounded-full blur-[120px] opacity-50" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-[600px] h-[600px] bg-[var(--chart-2)]/5 rounded-full blur-[100px] opacity-40" />

      <div ref={ref} className={`container mx-auto px-4 md:px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {}
          <div className="flex flex-col gap-6">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                The Hidden Cost of <span className="text-[var(--primary)]">Dirty Panels</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Slide to see the difference. A clean solar array captures maximum sunlight, while accumulated grime acts as a physical barrier to your energy production.
              </p>
            </div>

            {}
            <div className="relative w-full aspect-[4/3] md:aspect-square lg:aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl border border-border/50 group bg-muted">
              {}
              <img src="https://brief-horse.websiteshop.au/wp-content/uploads/2026/03/ChatGPT-Image-Mar-14-2026-10_45_58-PM.png" alt="Dirty solar panels covered in dust" className="absolute inset-0 w-full h-full object-cover" />
              
              {}
              <img src="https://brief-horse.websiteshop.au/wp-content/uploads/2026/03/ChatGPT-Image-Mar-14-2026-10_41_30-PM.png" alt="Clean, shining solar panels" className="absolute inset-0 w-full h-full object-cover" style={{
              clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`
            }} />

              {}
              <input type="range" min="0" max="100" value={sliderPosition} onChange={handleSliderChange} className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-20" aria-label="Compare dirty and clean solar panels" />

              {}
              <div className="absolute top-0 bottom-0 w-1 bg-white pointer-events-none z-10 shadow-[0_0_10px_rgba(0,0,0,0.3)]" style={{
              left: `${sliderPosition}%`
            }}>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border border-border/50 transition-transform group-hover:scale-110">
                  <ChevronsLeftRight className="w-5 h-5 text-foreground/70" />
                </div>
              </div>

              {}
              <div className="absolute bottom-4 left-4 px-3 py-1.5 rounded-full bg-background/80 backdrop-blur-md text-xs font-semibold text-foreground shadow-sm pointer-events-none z-10">
                After Cleaning
              </div>
              <div className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full bg-background/80 backdrop-blur-md text-xs font-semibold text-foreground shadow-sm pointer-events-none z-10">
                Before
              </div>
            </div>
          </div>

          {}
          <div className="flex flex-col gap-10 lg:pt-4">
            
            {}
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold text-foreground flex items-center gap-2">
                <Zap className="w-6 h-6 text-[var(--primary)]" />
                What's Blocking Your Power?
              </h3>
              <Accordion type="single" collapsible className="w-full bg-card rounded-2xl border border-border/50 shadow-sm px-4 py-2">
                {debrisTypes.map((item, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-border/50">
                    <AccordionTrigger className="text-base hover:no-underline hover:text-[var(--primary)] transition-colors py-5">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-muted text-muted-foreground">
                          <item.icon className="w-5 h-5" />
                        </div>
                        {item.title}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed pb-5 pl-14">
                      {item.content}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>

            {}
            <div className="space-y-6">
              <div className="space-y-2">
                <h3 className="text-2xl font-semibold text-foreground">
                  The ROI of Regular Cleaning
                </h3>
                <p className="text-sm text-muted-foreground">
                  Projected efficiency retention over 6 months without maintenance.
                </p>
              </div>
              
              <div className="p-6 rounded-3xl bg-card border border-border/50 shadow-xl shadow-[var(--shadow-color)]/5">
                <ChartContainer config={chartConfig} className="h-[250px] w-full">
                  <AreaChart accessibilityLayer data={chartData} margin={{
                  top: 10,
                  right: 10,
                  left: -20,
                  bottom: 0
                }}>
                    <defs>
                      <linearGradient id="fillClean" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-clean)" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="var(--color-clean)" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="fillDirty" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-dirty)" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="var(--color-dirty)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" opacity={0.5} />
                    <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={12} tick={{
                    fill: 'var(--muted-foreground)',
                    fontSize: 12
                  }} />
                    <YAxis tickLine={false} axisLine={false} tickMargin={12} tick={{
                    fill: 'var(--muted-foreground)',
                    fontSize: 12
                  }} tickFormatter={value => `${value}%`} domain={[60, 100]} />
                    <ChartTooltip cursor={{
                    stroke: 'var(--border)',
                    strokeWidth: 1,
                    strokeDasharray: '4 4'
                  }} content={<ChartTooltipContent indicator="dot" />} />
                    <Area type="monotone" dataKey="clean" stroke="var(--color-clean)" strokeWidth={3} fill="url(#fillClean)" activeDot={{
                    r: 6,
                    fill: "var(--color-clean)",
                    stroke: "var(--background)",
                    strokeWidth: 2
                  }} />
                    <Area type="monotone" dataKey="dirty" stroke="var(--color-dirty)" strokeWidth={3} fill="url(#fillDirty)" activeDot={{
                    r: 6,
                    fill: "var(--color-dirty)",
                    stroke: "var(--background)",
                    strokeWidth: 2
                  }} />
                    <ChartLegend content={<ChartLegendContent />} className="mt-4" />
                  </AreaChart>
                </ChartContainer>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>;
}